import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    MatrixConfiguration,
    MatrixConfigurations,
    MatrixConfigurationViewModel,
    MatrixDefinition,
    MatrixDefinitions,
    MatrixDefinitionViewModel,
    MatrixExportDefinition,
    MatrixViewModel
} from '../../../../Models/MatrixViewModel';
import { QueryViewModel } from '../../../../Models/QueryViewModel';
import { TenantService } from '../../../../Services';

export const highSeverityMatrixQuery = {
    QueryId: '00000000-0000-0000-0001-200000000121',
    FieldAlias: 'PotentialSeverityAliases',
    FieldType: 'SelectedItemsAsStrings',
    ModuleId: 'AI'
};

@Injectable({
    providedIn: 'root'
})

export class MatrixService {

    constructor(private tenantService: TenantService) { }

    validateData(data: MatrixExportDefinition) {
        const invalidData = {};
        const state = {};
        let valid = true;
        const fieldToCheck = 'Alias';
        this.checkForDuplicates(data.definitionHeader, fieldToCheck, state, invalidData);
        data.definitionBody.forEach(definitionRow => this.checkForDuplicates(definitionRow, fieldToCheck, state, invalidData));
        this.checkForDuplicates(data.configurationHeader, fieldToCheck, state, invalidData);
        data.configurationBody.forEach(configurationRow => this.checkForDuplicates(configurationRow, fieldToCheck, state, invalidData));

        for (const invalidDataKey in invalidData) {
            if (invalidData.hasOwnProperty(invalidDataKey)) {
                state[invalidDataKey].forEach(item => {
                    if (!item.errors) {
                        item.errors = [];
                        item.errors.push(fieldToCheck);
                    } else {
                        item.errors.push(fieldToCheck);
                    }
                    valid = false;
                });
            }
        }
        return valid ? null : { invalidData, state };
    }

    getMatrix(name: string): Observable<MatrixExportDefinition> {
        return this.tenantService.getMatrix(name, 'latest')
            .pipe(
                map((result) => {
                    const definitions = this.mapDefinitionsFromModel(result);
                    const configurations = this.mapConfigurationsFromModel(result);
                    return { ...definitions, ...configurations };
                }));
    }

    getSeverityMatrixQuery(): Observable<QueryViewModel> {
        return this.tenantService.getQuery(highSeverityMatrixQuery.ModuleId, highSeverityMatrixQuery.QueryId).pipe(map((result) => result));
    }

    saveSeverityMatrixQuery(matrixConfiguration: MatrixConfiguration[][], queryViewModel: QueryViewModel): Observable<any> {
        this.updateMatrixSeverityDefinitionFields(matrixConfiguration, queryViewModel);
        return this.tenantService.updateQuery(queryViewModel, highSeverityMatrixQuery.ModuleId, highSeverityMatrixQuery.QueryId).pipe(map((result) => result));
    }

    mapDefinitionsFromModel(matrix: MatrixViewModel): MatrixDefinitions {
        const data: MatrixDefinitions = {
            definitionHeader: [],
            definitionBody: []
        };
        matrix.Definitions.map(definition => {
            if (definition.RowPosition === 0) {
                data.definitionHeader[definition.ColumnPosition - 1] = {
                    Alias: definition.Alias
                };
            } else {
                const row = data.definitionBody[definition.RowPosition - 1] || [];
                row[definition.ColumnPosition - 1] = {
                    Alias: definition.Alias
                };
                data.definitionBody[definition.RowPosition - 1] = row;
            }
        });
        return data;
    }

    mapConfigurationsFromModel(matrix: MatrixViewModel): MatrixConfigurations {
        const data: MatrixConfigurations = {
            configurationHeader: [],
            configurationBody: []
        };
        matrix.Configurations.map(configuration => {
            if (configuration.RowPosition === 0) {
                data.configurationHeader[configuration.ColumnPosition - 1] = {
                    Alias: configuration.Alias,
                    Colour: configuration.Colour,
                    Score: configuration.Score,
                    IncludedInQuery: configuration.IncludedInQuery

                };
            } else {
                const row = data.configurationBody[configuration.RowPosition - 1] || [];
                row[configuration.ColumnPosition - 1] = {
                    Alias: configuration.Alias,
                    Colour: configuration.Colour,
                    Score: configuration.Score,
                    IncludedInQuery: configuration.IncludedInQuery
                };
                data.configurationBody[configuration.RowPosition - 1] = row;
            }
        });
        return data;
    }


    getMatrixSeverityDefinitionFields(viewModel: QueryViewModel): string[] {
        return viewModel.QueryModel[highSeverityMatrixQuery.FieldAlias]?.[highSeverityMatrixQuery.FieldType]?.splice(',') ?? [];
    }

    updateMatrixSeverityDefinitionFields(matrixConfiguration: MatrixConfiguration[][], queryViewModel: QueryViewModel) {
        const listOfAliases = [];
        for (const row of matrixConfiguration) {
            for (const col of row) {
                if (col.IncludedInQuery) {
                    listOfAliases.push(`Potential_${col.Alias}`);
                    listOfAliases.push(`${col.Alias}`);
                }
            }
        }
        queryViewModel.QueryModel[highSeverityMatrixQuery.FieldAlias][highSeverityMatrixQuery.FieldType] = listOfAliases;
    }

    mergeConfigurations(data: MatrixConfigurations): MatrixConfigurationViewModel[] {
        const configurations = data.configurationHeader.map((col, inx) => ({
            RowPosition: 0,
            ColumnPosition: inx + 1,
            Alias: col.Alias,
            Colour: null,
            Score: null
        }));
        const bodyConfigurations = [].concat(...data.configurationBody.map((row, rowInx) => row.map((col, colInx) => ({
            RowPosition: rowInx + 1,
            ColumnPosition: colInx + 1,
            Alias: col.Alias,
            Score: col.Score,
            Colour: col.Colour
        }))));
        const merged = configurations.concat(bodyConfigurations).sort(this.sortByColRow);
        return merged as MatrixConfigurationViewModel[];
    }

    mergeDefinitions(data: MatrixDefinitions): MatrixDefinitionViewModel[] {
        const definitions = data.definitionHeader.map((col, inx) => ({
            RowPosition: 0,
            ColumnPosition: inx + 1,
            Alias: col.Alias,
        }));
        const bodyDefinitions = [].concat(...data.definitionBody.map((row, rowInx) => row.map((col, colInx) => ({
            RowPosition: rowInx + 1,
            ColumnPosition: colInx + 1,
            Alias: col.Alias,
        }))));

        return definitions.concat(bodyDefinitions).sort(this.sortByColRow);
    }

    publishMatrix(name: string, data: MatrixExportDefinition) {
        const saveMatrix: MatrixViewModel = new class implements MatrixViewModel {
            Configurations: MatrixConfigurationViewModel[];
            Definitions: MatrixDefinitionViewModel[];
        }();
        saveMatrix.Configurations = this.mergeConfigurations({
            configurationBody: data.configurationBody,
            configurationHeader: data.configurationHeader
        });
        saveMatrix.Definitions = this.mergeDefinitions({
            definitionBody: data.definitionBody,
            definitionHeader: data.definitionHeader
        });
        return this.tenantService.saveMatrix(name, saveMatrix);
    }

    sortByColRow(a, b) {
        if (a.ColumnPosition > b.ColumnPosition) {
            return 1;
        } else {
            if (a.ColumnPosition === b.ColumnPosition) {
                if (a.RowPosition > b.RowPosition) {
                    return 1;
                } else {
                    if (a.RowPosition === b.RowPosition) {
                        return 0;
                    } else {
                        return -1;
                    }
                }
            } else {
                return -1;
            }
        }
    }

    checkForDuplicates(items: (MatrixDefinition | MatrixConfiguration)[], fieldToCheck: string, state: any, duplicates: {}) {
        let valid = true;
        items.forEach(item => {
            const i = item as any;
            if (i.errors) {
                const index = i.errors.indexOf(fieldToCheck);
                if (index > -1) {
                    i.errors.splice(index, 1);
                    if (i.errors.length === 0) {
                        delete i.errors;
                    }
                }
            }
            if (state[item[fieldToCheck]]) {
                duplicates[item[fieldToCheck]] = {
                    field: fieldToCheck,
                    value: item[fieldToCheck]
                };
                state[item[fieldToCheck]].push(item);
                valid = false;
            } else {
                state[item[fieldToCheck]] = ([item] as any);
            }
        });
        return valid
            ? null
            : { invalidAlias: { valid: false, value: duplicates } };
    }
}

